import React, { useState } from 'react';
import { Paper, TextField, Grid, Button, Alert, Box, InputAdornment, IconButton } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';
import ImageSelector from '../components/ImageSelector';

const TopbarTab = () => {
  const { t } = useTranslation();
  const { config, setConfig, handleChange, isFieldConfigurable, isFieldFixed, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false);
  const [imageSelectorField, setImageSelectorField] = useState(null);

  // Campos del topbar
  const topbarFields = [
    { path: 'siteConfig.company.logo', label: t('topbar_logo') || 'Logo del Topbar', helperText: t('topbar_logo_helper') || 'Selecciona el logo que se mostrará en el topbar. Si no hay logo, se mostrará el nombre de la empresa.' },
    { path: 'siteConfig.company.topbarFontSizePx', label: t('topbar_font_size') || 'Tamaño de fuente del Topbar', helperText: t('topbar_font_size_helper') || 'Tamaño en píxeles para los links del topbar. Por defecto es 14px.', type: 'number', defaultValue: 14, inputProps: { min: 12, max: 20, step: 1 } }
  ];
  
  // Filtrar solo los campos configurables
  const fields = topbarFields.filter(field => isFieldConfigurable(field.path));

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Preparar updates solo para campos configurables
      const updates = {};
      fields.forEach(field => {
        const value = getFieldValue(field.path);
        updates[field.path] = field.type === 'number' ? Number(value || field.defaultValue || 0) : value;
      });

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables en esta sección');
        return;
      }

      const preparedUpdates = prepareFieldUpdates(updates);
      await SitesService.updateFields(preparedUpdates);
      
      // Recargar configuración para obtener valores actualizados
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving topbar:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  const getFieldValue = (path) => {
    const value = path.split('.').reduce((obj, key) => obj?.[key], config);
    // Retornar el valor o string vacío si es null/undefined
    const field = topbarFields.find((item) => item.path === path);
    if ((value === null || value === undefined || value === '') && field?.defaultValue !== undefined) {
      return field.defaultValue;
    }
    return value !== null && value !== undefined ? value : '';
  };

  const handleOpenImageSelector = (fieldPath) => {
    setImageSelectorField(fieldPath);
    setImageSelectorOpen(true);
  };

  const handleImageSelect = (imageUrl) => {
    if (imageSelectorField) {
      handleChange(imageSelectorField, imageUrl);
    }
    setImageSelectorOpen(false);
    setImageSelectorField(null);
  };

  const hasEditableFields = fields.length > 0;

  if (!hasEditableFields) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      <Grid container spacing={2}>
        {fields.map((field) => {
          const value = getFieldValue(field.path);
          const isImageField = field.path.includes('.logo') || field.path.includes('Image');

          return (
            <Grid item xs={12} key={field.path}>
              <TextField
                fullWidth
                type={field.type || 'text'}
                label={field.label}
                value={value}
                onChange={(e) => handleChange(field.path, e.target.value)}
                helperText={field.helperText}
                inputProps={field.inputProps}
                InputProps={isImageField ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleOpenImageSelector(field.path)}
                        edge="end"
                        color="primary"
                      >
                        <ImageIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                } : undefined}
              />
              {isImageField && value && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <img
                    src={value}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '8px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              )}
            </Grid>
          );
        })}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
              {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
            </Button>
          </Box>
        </Grid>
      </Grid>
      <ImageSelector
        open={imageSelectorOpen}
        onClose={() => {
          setImageSelectorOpen(false);
          setImageSelectorField(null);
        }}
        onSelect={handleImageSelect}
        currentValue={imageSelectorField ? getFieldValue(imageSelectorField) : ''}
        label={t('select_logo') || 'Seleccionar Logo'}
      />
    </Paper>
  );
};

export default TopbarTab;
